import { t } from 'i18next';
import { Vector3 } from 'three';

import { ContextMenuItem } from '../../../../../../../react/components/skyview/context-menu';
import { isDefined } from '../../../../../../../utils/variables';
import { RevertChange } from '../../../../../../elements/design/design-node/design-node';
import {
  breakCircle as breakCircleIntoArcs,
  DesignCircle,
  getQuarterConics,
  getSeamOffsets,
  reshapeCircle,
} from '../../../../../../elements/design/design-node/utils/circle';
import { getConicPoint } from '../../../../../../elements/design/design-node/utils/conic';
import { ActionInProgress, ActionName, DesignTool } from '../../../../../design-tool';
import { PickingLine } from '../../../../../picking-line';
import { addAndCommitAction } from '../../utils/actions';
import { getConicCursorVertices } from '../../utils/curve';
import { abortActionInProgress, applyActionInProgress, initActionInProgress } from '../general';

/**
 * A handle of a circle or ellipse: a corner at the end of an axis (a seam), or the middle of an
 * arc, by its index along the loop.
 */
export type CircleHandle = {
  circle: DesignCircle;
  kind: 'corner' | 'middle';
  index: number;
};

/** How close to a multiple of 45° the direction of a dragged corner snaps to it. */
const angleSnap = (3 * Math.PI) / 180;

const horizontal = (v: Vector3) => new Vector3(v.x, v.y, 0);

/**
 * Gets the direction from the center to the cursor, snapped to multiples of 45° when it is close,
 * or along the axis when it is locked.
 */
function getCornerDirection(toCursor: Vector3, axis: Vector3, locked: boolean): Vector3 {
  if (locked) {
    return axis.clone().normalize();
  }

  const angle = Math.atan2(toCursor.y, toCursor.x);
  const snapped = Math.round(angle / (Math.PI / 4)) * (Math.PI / 4);
  const direction = Math.abs(angle - snapped) < angleSnap ? snapped : angle;
  return new Vector3(Math.cos(direction), Math.sin(direction), 0);
}

/**
 * Changes the shape of a circle or ellipse by dragging a handle, see CircleHandle:
 * - a corner moves the end of its axis to the cursor, and the opposite end with it. The other axis
 *   turns with it and keeps its length, so dragging it sideways turns the ellipse. With Shift, it
 *   stays on its axis. The direction snaps to multiples of 45°.
 * - the middle of an arc scales the whole circle or ellipse.
 * The radius (the length of the dragged axis, or the longest one) can also be typed in the tooltip.
 */
export const resizeCircle = (tool: DesignTool, handle: CircleHandle): ContextMenuItem => {
  const { circle, kind, index } = handle;
  const disabled = circle.seams.length !== 4;

  return {
    title: t('contextMenu.resizeCircle', { ns: 'skyviewDesign' }),
    action: () => {
      if (disabled) {
        return;
      }

      const node = tool.asset.designNode;
      const center = node.getPointById(circle.center).vertex.clone();
      const offsets = getSeamOffsets(node, circle).map(horizontal);
      const shapeCursor = new PickingLine();

      // The radius (meters), when it is typed.
      let typedRadius: number | undefined;

      const getCursor = () =>
        tool.currentPoint && horizontal(node.getLocalPosition(tool.currentPoint).sub(center));

      /**
       * The offsets of the seams from the center for the cursor, in local coordinates.
       */
      const getOffsets = (): Vector3[] | undefined => {
        const toCursor = getCursor();

        if (kind === 'middle') {
          const middle = horizontal(
            getConicPoint(getQuarterConics(new Vector3(), offsets)[index], 0.5),
          );
          const longest = Math.max(...offsets.map((x) => x.length()));
          const scale = isDefined(typedRadius)
            ? typedRadius / longest
            : toCursor && toCursor.length() / middle.length();
          return isDefined(scale) && scale > 0
            ? offsets.map((x) => x.clone().multiplyScalar(scale))
            : undefined;
        }

        const axis = offsets[index];
        const next = offsets[(index + 1) % 4];
        const locked = tool.inputHandler.shiftIsDown;

        if (!toCursor && !isDefined(typedRadius)) {
          return undefined;
        }

        const direction = getCornerDirection(toCursor ?? axis, axis, locked);
        const length = typedRadius ?? (locked ? toCursor!.dot(direction) : toCursor!.length());

        if (length <= 0) {
          return undefined;
        }

        // The other axis is a quarter turn on, the way the loop goes.
        const turn = Math.sign(axis.x * next.y - axis.y * next.x);
        const end = direction.clone().multiplyScalar(length);
        const other = new Vector3(-direction.y * turn, direction.x * turn, 0).multiplyScalar(
          next.length(),
        );

        const result: Vector3[] = [];
        [end, other, end.clone().negate(), other.clone().negate()].forEach((x, i) => {
          result[(index + i) % 4] = x;
        });
        return result;
      };

      const action: ActionInProgress = {
        actionName: ActionName.ResizeCircle,
        initiate: () => {
          tool.add(shapeCursor);
          tool.curveDistanceInput = {
            kind: 'radius',
            shape: 'circle',
            getDistance: () => {
              const current = getOffsets() ?? offsets;
              return kind === 'middle'
                ? Math.max(...current.map((x) => x.length()))
                : current[index].length();
            },
            setDistance: (distance) => {
              typedRadius = distance;
              action.refresh?.();
              tool.updateTooltip();
            },
            apply: () => void applyActionInProgress(tool).action(),
            cancel: () => void abortActionInProgress(tool).action(),
          };
          // Shows the radius in the tooltip right away, where it can be typed.
          tool.updateTooltip();
          tool.onEdited?.();
        },
        cleanup: () => {
          tool.remove(shapeCursor);
          tool.curveDistanceInput = undefined;
        },
        refresh: () => {
          const shape = getOffsets();
          const conics = shape
            ? getQuarterConics(center, shape).map((conic) => ({
                ...conic,
                start: node.getWorldPosition(conic.start),
                control: node.getWorldPosition(conic.control),
                end: node.getWorldPosition(conic.end),
              }))
            : [];
          shapeCursor.setVertices(getConicCursorVertices(conics));
        },
        readyToApply: () => isDefined(getOffsets()),
        apply: () => {
          const shape = getOffsets();

          if (!shape) {
            return;
          }

          let changes: RevertChange[] = [];

          addAndCommitAction(tool, {
            commit: () => {
              changes = reshapeCircle(node, circle, shape);
              node.refresh();
              tool.asset.refreshMetadata();
            },
            rollback: () => {
              node.revert(changes);
              node.refresh();
              tool.asset.refreshMetadata();
            },
          });
        },
      };

      initActionInProgress(tool, action);
      tool.contextMenuCallback?.();
    },
    disabled,
    hotkey: 'M',
  };
};

/**
 * Breaks a circle or ellipse into arcs and points like any other, as an undoable action, e.g. when
 * a handle of it is clicked to create geometry there.
 */
export function breakCircle(tool: DesignTool, circle: DesignCircle) {
  const node = tool.asset.designNode;
  let changes: RevertChange[] = [];

  addAndCommitAction(tool, {
    commit: () => {
      changes = breakCircleIntoArcs(node, circle);
      node.refresh();
    },
    rollback: () => {
      node.revert(changes);
      node.refresh();
    },
  });
}
